import { ordenarArrayObjetosByString, borrarObjetosDuplicados, ordenarArrayObjetosByDate } from "../../utils/funcionalidadesArrays";
import dayjs from "dayjs";

// Ordena prestadores por próximo turno (note, fecha ISO) y luego por nombre
export function ordenarPrestadoresPorProximoTurno(prestadores) {
  return prestadores.slice().sort((a, b) => {
    // Si ambos tienen fecha
    if (a.proximoTurnoFecha && b.proximoTurnoFecha) {
      const fechaA = dayjs(a.proximoTurnoFecha);
      const fechaB = dayjs(b.proximoTurnoFecha);
      if (fechaA.isBefore(fechaB)) return -1;
      if (fechaA.isAfter(fechaB)) return 1;
      // Si la fecha es igual, ordenar por nombre
      return (a.text || a.nombre || '').localeCompare(b.text || b.nombre || '');
    }
    // Si solo uno tiene fecha, ese va primero
    if (a.proximoTurnoFecha && !b.proximoTurnoFecha) return -1;
    if (!a.proximoTurnoFecha && b.proximoTurnoFecha) return 1;
    // Si ninguno tiene fecha, ordenar por nombre
    return (a.text || a.nombre || '').localeCompare(b.text || b.nombre || '');
  });
}

// Helpers y utilidades para NuevoTurno
const diasSemana = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

/**
 * Valida si un horario está disponible para un prestador en un día de la semana
 */
export const validarHorarioDisponible = (diaSemana, horarioPrestador) => {
  const diaActual = diasSemana[diaSemana].toLowerCase();
  const horarios = {
    domingo: horarioPrestador.domWebM !== 0 || horarioPrestador.domWebT !== 0,
    lunes: horarioPrestador.lunWebM !== 0 || horarioPrestador.lunWebT !== 0,
    martes: horarioPrestador.marWebM !== 0 || horarioPrestador.marWebT !== 0,
    miercoles: horarioPrestador.mieWebM !== 0 || horarioPrestador.mieWebT !== 0,
    jueves: horarioPrestador.jueWebM !== 0 || horarioPrestador.jueWebT !== 0,
    viernes: horarioPrestador.vieWebM !== 0 || horarioPrestador.vieWebT !== 0,
    sabado: horarioPrestador.sabWebM !== 0 || horarioPrestador.sabWebT !== 0,
  };
  return horarios[diaActual] || false;
};

/**
 * Fusiona y filtra prestadores particulares y regulares, eliminando duplicados y ordenando
 */
export const procesarPrestadores = (prestadoresParticulares, prestadoresRegulares) => {
  let prestadoresDisponiblesTmp = [...prestadoresParticulares, ...prestadoresRegulares];
  let prestadoresDisponiblesTmpFiltrados = [];
  prestadoresDisponiblesTmp.forEach((prestador) => {
    let prestadorCod = prestador.codigo.split("-")[0];
    if (
      [...prestadoresDisponiblesTmpFiltrados.map(
        (presFiltrado) => presFiltrado.codigo.split("-")[0]
      )].includes(prestadorCod)
    ) {
      let index = prestadoresDisponiblesTmpFiltrados.findIndex(
        (presFiltrado) => presFiltrado.codigo.split("-")[0] === prestadorCod
      );
      const existente = prestadoresDisponiblesTmpFiltrados[index];
      const existenteEsParticular = existente?.mutual === 1;
      const nuevoEsParticular = prestador?.mutual === 1;
      // Si hay duplicado, priorizar la mutual del paciente (no particular)
      // para evitar que se marque como Particular cuando también atiende por obra social.
      if (existenteEsParticular && !nuevoEsParticular) {
        prestadoresDisponiblesTmpFiltrados[index] = {
          ...existente,
          ...prestador,
        };
      } else if (!existenteEsParticular && nuevoEsParticular) {
        // Mantener el existente (obra social) y no reemplazar por particular.
      } else if ([89, 75].includes(+prestadorCod)) {
        prestadoresDisponiblesTmpFiltrados.push({
          ...existente,
          ...prestador,
          note2Color: prestador.mutual === 1 ? "#3A3D99" : "#000000",
          note2: prestador.mutual === 1 ? "POR ATENCION PARTICULAR" : "",
        });
      }
    } else {
      prestadoresDisponiblesTmpFiltrados.push(prestador);
    }
  });
  prestadoresDisponiblesTmpFiltrados = ordenarArrayObjetosByString(
    prestadoresDisponiblesTmpFiltrados,
    "text"
  );
  return prestadoresDisponiblesTmpFiltrados;
};

export const filtrarTurnosPorFechaYHora = (turnos) => {
  return turnos.filter(
    (turno) =>
      dayjs(turno.fecha).isSame(dayjs()) ||
      (dayjs(turno.fecha).isAfter(dayjs()) &&
        (dayjs().format("YYYY-MM-DD") !==
          dayjs(turno.fecha).format("YYYY-MM-DD") ||
          (dayjs().format("YYYY-MM-DD") ===
            dayjs(turno.fecha).format("YYYY-MM-DD") &&
            dayjs(turno.hora).format("HH:mm") >
              dayjs().add(3, "hour").format("HH:mm"))))
  );
};

export const filtrarTurnosPorRestricciones = (
  turnos,
  atiende,
  turnosDadosMutual,
  usuario
) => {
  return turnos.filter((turno) => {
    if (atiende.length === 0) {
      return true;
    } else {
      const diaSemana = dayjs(turno.fecha).get("d");
      const cantidadTurnosDia =
        atiende[0][diasSemana[diaSemana].toLowerCase()];
      const turnosOcupadosDia = turnosDadosMutual.find(
        (turnoMutual) => turnoMutual.semana === dayjs(turno.fecha).week()
      )[diasSemana[diaSemana].toLowerCase()];

      return turnosOcupadosDia < cantidadTurnosDia;
    }
  });
};

export const obtenerFechasTurnos = (turnosFiltrados) => {
  const fechasTurnos = borrarObjetosDuplicados(turnosFiltrados, "fecha", true)
    .filter((turno) => turno)
    .map((turno) => ({
      fecha: turno,
    }));
  return fechasTurnos;
};

export const obtenerProximosTurnos = (
  fechasTurnos,
  horarios,
  turnosFiltradosPorRestricciones
) => {
  const turnosProximos = [];
  fechasTurnos.forEach((fecha) => {
    const diaSemana = dayjs(fecha.fecha).get("d");
    const fechaTurno = dayjs(fecha.fecha).format("YYYY-MM-DD");
    const cantTurnos = turnosFiltradosPorRestricciones.filter((turno) =>
      dayjs(turno.fecha).isSame(dayjs(fecha.fecha))
    ).length;
    
    if (validarHorarioAtencion(diaSemana, horarios)) {
      turnosProximos.push({
        date: fechaTurno,
        cantTurnos: cantTurnos,
        textColor: "#ffffff",
        backgroundColor: "#3A3D99",
      });
    }
  });
  return turnosProximos;
};

const validarHorarioAtencion = (diaSemana, horarios) => {
  const diaActual = diasSemana[diaSemana].toLowerCase();
  
  const tieneHorario = 
    (horarios[diaActual + "MDe"] !== "" && horarios[diaActual + "MHa"]) ||
    (horarios[diaActual + "TDe"] !== "" && horarios[diaActual + "THa"] !== "");

  if (!tieneHorario) return false;

  const atencionWeb = {
    domingo: horarios.domWebM !== 0 || horarios.domWebT !== 0,
    lunes: horarios.lunWebM !== 0 || horarios.lunWebT !== 0,
    martes: horarios.marWebM !== 0 || horarios.marWebT !== 0,
    miercoles: horarios.mieWebM !== 0 || horarios.mieWebT !== 0,
    jueves: horarios.jueWebM !== 0 || horarios.jueWebT !== 0,
    viernes: horarios.vieWebM !== 0 || horarios.vieWebT !== 0,
    sabado: horarios.sabWebM !== 0 || horarios.sabWebT !== 0,
  };

  return atencionWeb[diaActual] || false;
};

export const verificarInasistenciasPorEspecialidad = (especialidad, turnosPaciente) => {
  const turnosPasados = turnosPaciente;
  let inasistidos = 0;

  turnosPasados.forEach((turno) => {
    if (
      (dayjs(turno.fecha).isSame(new dayjs()) &&
        dayjs(turno.hora).format("HH:mm") >=
          dayjs("0001-01-01T00:00:00").format("HH:mm")) ||
      (dayjs(turno.fecha).isAfter(new dayjs().subtract(2, "month")) &&
        dayjs(turno.fecha).isBefore(new dayjs()))
    ) {
      if (turno.especialidad === especialidad && turno.asistio === false) {
        inasistidos += 1;
      }
    }
  });

  return inasistidos > 1;
};

export const verificarTurnoConPrestadorPendiente = (prestador, turnosPaciente) => {
  const turnosFuturos = turnosPaciente;
  let turnoPendiente = false;
  
  turnosFuturos.forEach((turno) => {
    if (
      (dayjs(turno.fecha).isSame(new dayjs()) &&
        dayjs(turno.hora).format("HH:mm") <=
          dayjs("0001-01-01T00:00:00").format("HH:mm")) ||
      (dayjs(turno.fecha).isBefore(new dayjs().add(2, "month")) &&
        dayjs(turno.fecha).isAfter(new dayjs()))
    ) {
      if (turno.prestadorCod === prestador) {
        turnoPendiente = true;
      }
    }
  });

  return turnoPendiente;
};

